import { api } from './api';
import type { Lectura } from '@suminuta/shared';

/**
 * Kardex global de movimientos (PROMPT_N°053).
 */

export interface FiltrosKardex {
  desde?: string;
  hasta?: string;
  tipo?: 'entrada' | 'salida' | 'ajuste';
  motivo?: string;
  ingrediente_id?: number;
  usuario_id?: number;
  q?: string;
  page?: number;
  limit?: number;
}

export interface MovimientoKardex {
  id: number;
  fecha: string;
  ingrediente: { id: number; nombre: string; unidad: string };
  tipo: 'entrada' | 'salida' | 'ajuste';
  motivo: string;
  motivo_label: string;
  cantidad: number;
  cantidad_base: number | null;
  stock_anterior: number;
  stock_nuevo: number;
  lote: { id: number; numero_lote: string } | null;
  referencia: string | null;
  usuario: { id: number; nombre: string };
  notas: string | null;
  costo_total: number | null;
  movimiento_revertido_id: number | null;
  revertido_por_id: number | null;
}

/** Agregados del rango completo, no de la página (N°056). */
export interface AgregadosKardex {
  usuarios: number;
  ajustes: number;
  salidas: number;
  salidas_revertidas: number;
  mas_movido: { id: number; nombre: string; movimientos: number } | null;
  ajustes_por_usuario: { usuario: string; n: number }[];
}

export interface ResumenKardex {
  entradas: { n: number; total_base: number };
  salidas: { n: number; total_base: number };
  /** Los reversos no se suman a entradas: ver el comentario del backend. */
  reversos: { n: number; total_base: number };
  ajustes: { n: number };
}

export interface RespuestaKardex {
  data: MovimientoKardex[];
  resumen: ResumenKardex;
  agregados: AgregadosKardex;
  lectura: Lectura;
  rango: { desde: string; hasta: string; zona: string; offset: string };
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasMore: boolean;
  };
}

/** Pasa los filtros a query string, salteando los vacíos. */
export const filtrosAQuery = (f: FiltrosKardex): URLSearchParams => {
  const p = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.append(k, String(v));
  });
  return p;
};

export const movimientosService = {
  listar: async (filtros: FiltrosKardex): Promise<RespuestaKardex> => {
    const { data } = await api.get<RespuestaKardex>(
      `/movimientos?${filtrosAQuery(filtros).toString()}`
    );
    return data;
  },

  /**
   * Descarga el CSV. Se pide como blob y se dispara con un `<a>` temporal:
   * un `window.open` perdería el header Authorization y el backend
   * respondería 401.
   */
  exportar: async (filtros: FiltrosKardex): Promise<void> => {
    const { page, limit, ...sinPaginar } = filtros;
    const query = filtrosAQuery(sinPaginar).toString();
    const resp = await api.get(`/movimientos/export?${query}`, {
      responseType: 'blob',
    });

    const nombre =
      `movimientos_bodega_${sinPaginar.desde ?? ''}_${sinPaginar.hasta ?? ''}.csv`.replace(
        /__+/g,
        '_'
      );
    const url = window.URL.createObjectURL(new Blob([resp.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = nombre;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

export default movimientosService;
